import { api } from './api';
import type { TimeEntry } from './timeEntriesStore';

interface ApiFeeRecord {
  id: string;
  feeType: string;
  description: string;
  feeDate: string;
  durationMinutes: number | null;
  rate: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
  isBillable: boolean;
  feeNoteId: string | null; // set when the record has been invoiced
  createdAt: string;
  case: { id: string; caseNumber: string; caseName: string } | null;
  barrister: { id: string; user: { id: string; firstName: string; lastName: string; memberType: string } } | null;
}

function feeTypeLabel(t: string): string {
  const map: Record<string, string> = {
    brief_fee: 'Brief fee', refresher: 'Refresher', hourly: 'Hourly', fixed: 'Fixed',
    conference: 'Conference', opinion: 'Opinion', cfa: 'CFA', dba: 'DBA', pro_bono: 'Pro bono',
  };
  return map[t] ?? t;
}

function apiToTimeEntry(f: ApiFeeRecord): TimeEntry {
  const rate = Number(f.rate ?? 0);
  const amount = Number(f.amount);
  const hours = f.durationMinutes ? (f.durationMinutes / 60) : (rate > 0 ? amount / rate : 0);
  const barName = f.barrister ? `${f.barrister.user.firstName} ${f.barrister.user.lastName}` : '';
  const caseLabel = f.case ? `${f.case.caseNumber} ${f.case.caseName}` : '';

  return {
    id: f.id,
    date: new Date(f.feeDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    matterId: f.case?.id ?? '',
    matterLabel: caseLabel,
    barrister: barName,
    kind: feeTypeLabel(f.feeType),
    hours: hours.toFixed(2),
    amount,
    rate,
    vat: Number(f.vatAmount),
    status: f.feeNoteId ? 'Billed' : f.isBillable ? 'Billable' : 'Non-billable',
    description: f.description,
    createdAt: new Date(f.createdAt).getTime(),
    feeType: f.feeType,
    feeDateIso: f.feeDate ? new Date(f.feeDate).toISOString().slice(0, 10) : undefined,
    isBillable: f.isBillable,
    durationMinutes: f.durationMinutes,
  };
}

export async function fetchFeeRecords(opts?: { caseId?: string; unbilled?: 'true' | 'false' }): Promise<TimeEntry[]> {
  const res = await api.get('/fees', {
    params: {
      limit: 100, sortBy: 'feeDate', sortOrder: 'desc',
      ...(opts?.caseId && { caseId: opts.caseId }),
      ...(opts?.unbilled && { unbilled: opts.unbilled }),
    },
  });
  const payload = res.data?.data ?? res.data;
  const records: ApiFeeRecord[] = payload.feeRecords ?? [];
  return records.map(apiToTimeEntry);
}

export async function deleteFeeRecord(id: string): Promise<void> {
  await api.delete(`/fees/${id}`);
}

/**
 * Edit an existing fee record — PATCH /fees/:id (barrister and case are fixed at creation).
 * The API refuses changes to money fields once the record is on a fee note (a credit note is the
 * correction); description and date stay correctable. The endpoint existed with no client.
 */
export type UpdateFeeRecordPayload = Partial<Omit<CreateFeeRecordPayload, 'caseId' | 'barristerId'>>;

export async function updateFeeRecord(id: string, payload: UpdateFeeRecordPayload): Promise<TimeEntry> {
  const res = await api.patch(`/fees/${id}`, payload);
  const record: ApiFeeRecord = (res.data as any).data ?? res.data;
  return apiToTimeEntry(record);
}

export interface CreateFeeRecordPayload {
  caseId: string;
  barristerId: string;
  feeType: 'brief_fee' | 'refresher' | 'hourly' | 'fixed' | 'conference' | 'opinion' | 'cfa' | 'dba' | 'pro_bono';
  description: string;
  feeDate: string; // ISO date (YYYY-MM-DD)
  amount: number;
  durationMinutes?: number;
  rate?: number;
  isBillable?: boolean;
}

export async function createFeeRecord(payload: CreateFeeRecordPayload): Promise<TimeEntry> {
  const res = await api.post('/fees', payload);
  const record: ApiFeeRecord = (res.data as any).data ?? res.data;
  return apiToTimeEntry(record);
}

export interface FeeMatterSummaryRow {
  matterId: string;
  matterNumber: string;
  matterName: string;
  status: string;
  unbilled: number;
  invoiced: number;
  paid: number;
  outstanding: number;
  currency: string;
}

export async function fetchFeesSummaryByMatter(): Promise<FeeMatterSummaryRow[]> {
  const res = await api.get('/fees/summary-by-matter');
  return (res.data as any).data ?? [];
}
